import { Box, Divider, Grid2, InputLabel, TextField, Typography } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { FC, useMemo } from "react";

import { IInterceptorRowDetails, ISoftwareStatusDetails, SoftwareStatusType } from ".";
import { AGDataGrid } from "../../../components/AGDataGrid";
import { AGDialog } from "../../../components/AGDialog";
import "../index.css";
import { useGetJurisdictionsListQuery } from "../../../redux/reducers/jurisdictions.reducer";
import { JURISDICTION_TYPE_E } from "../../../types";

interface IDeviceSettingsDialogProps {
	open: boolean;
	deviceDetails: IInterceptorRowDetails | null;
	onClose: () => void;
}

const DeviceSettingsDialog: FC<IDeviceSettingsDialogProps> = (props) => {
	const { open, deviceDetails, onClose } = props;

	// GET JURISDICTIONS LIST
	const { data: jurisdictionsList } = useGetJurisdictionsListQuery({
		all: true,
		jurisdiction_type: JURISDICTION_TYPE_E.RTOS
	});

	const selectedJurisdiction = useMemo(
		() => jurisdictionsList?.results?.find((item) => item.id === deviceDetails?.rto) ?? null,
		[deviceDetails?.rto, jurisdictionsList?.results]
	);

	const systemTemperatureColumnsData: GridColDef[] = [
		{
			field: "temperature",
			headerName: "Temperature",
			flex: 1,
			sortable: false,
			cellClassName: "device-settings-table-cell border",
			align: "center",
			headerAlign: "center"
		},
		{
			field: "current",
			headerName: "Current",
			flex: 1,
			sortable: false,
			cellClassName: "device-settings-table-cell border",
			align: "center",
			headerAlign: "center"
		},
		{
			field: "maximum",
			headerName: "Maximum",
			flex: 1,
			sortable: false,
			cellClassName: "device-settings-table-cell",
			align: "center",
			headerAlign: "center"
		}
	];

	const memoryUsageColumnsData: GridColDef[] = [
		{
			field: "type",
			headerName: "Type",
			flex: 1,
			sortable: false,
			cellClassName: "device-settings-table-cell border",
			align: "center",
			headerAlign: "center"
		},
		{
			field: "current",
			headerName: "Current",
			flex: 1,
			sortable: false,
			cellClassName: "device-settings-table-cell border",
			align: "center",
			headerAlign: "center"
		},
		{
			field: "total",
			headerName: "Total",
			flex: 1,
			sortable: false,
			cellClassName: "device-settings-table-cell",
			align: "center",
			headerAlign: "center"
		}
	];

	const softwareStatusColumnsData: GridColDef[] = [
		{
			field: "software",
			headerName: "Software",
			flex: 1,
			sortable: false,
			cellClassName: "device-settings-table-cell border",
			align: "center",
			headerAlign: "center"
		},
		{
			field: "status",
			headerName: "Status",
			flex: 1,
			sortable: false,
			cellClassName: "device-settings-table-cell border",
			headerAlign: "center",
			renderCell: (params: GridRenderCellParams<ISoftwareStatusDetails, SoftwareStatusType>) => (
				<Box sx={{ width: "100%", height: "100%", paddingY: "0.5rem" }}>
					<Box className={`health-check-table-status-cell ${params.value}`}>
						<Typography variant="body2" textAlign="center" textTransform="capitalize">
							{params.value}
						</Typography>
					</Box>
				</Box>
			)
		},
		{
			field: "uptime",
			headerName: "Uptime",
			flex: 1,
			sortable: false,
			cellClassName: "device-settings-table-cell",
			align: "center",
			headerAlign: "center"
		}
	];

	return (
		<AGDialog open={open} onClose={onClose} title="Device" maxWidth="xl" color="primary">
			<Box className="device-settings-section">
				<Typography variant="h6" color="var(--color-primary-main)">
					Information
				</Typography>

				<Grid2 container spacing={2} width="100%">
					<Grid2 size={{ xs: 12, md: 5, xl: 4 }}>
						<InputLabel htmlFor="serial-number-input" sx={{ fontWeight: 500, marginBottom: "0.5rem" }}>
							Serial Number
						</InputLabel>

						<TextField
							fullWidth
							variant="outlined"
							color="primary"
							id="serial-number-input"
							size="small"
							value={deviceDetails?.device_serial_number ?? ""}
							slotProps={{ input: { readOnly: true } }}
							autoComplete="off"
						/>
					</Grid2>

					<Grid2 size={{ xs: 12, md: 5, xl: 4 }}>
						<InputLabel htmlFor="device-id-input" sx={{ fontWeight: 500, marginBottom: "0.5rem" }}>
							Device Name
						</InputLabel>

						<TextField
							fullWidth
							variant="outlined"
							color="primary"
							id="device-id-input"
							size="small"
							autoComplete="off"
							value={deviceDetails?.device_name ?? ""}
							slotProps={{ input: { readOnly: true } }}
						/>
					</Grid2>

					<Grid2 size={{ xs: 12, md: 5, xl: 4 }}>
						<InputLabel htmlFor="system-version-input" sx={{ fontWeight: 500, marginBottom: "0.5rem" }}>
							System Version
						</InputLabel>

						<TextField
							fullWidth
							variant="outlined"
							color="primary"
							id="system-version-input"
							size="small"
							value={deviceDetails?.device_system_version ?? ""}
							slotProps={{ input: { readOnly: true } }}
							autoComplete="off"
						/>
					</Grid2>

					<Box sx={{ width: "100%", display: { xs: "none", md: "block", xl: "none" } }} />

					<Grid2 size={{ xs: 12, md: 5, xl: 4 }}>
						<InputLabel htmlFor="local-ip-address-input" sx={{ fontWeight: 500, marginBottom: "0.5rem" }}>
							Local IP Address
						</InputLabel>

						<TextField
							fullWidth
							variant="outlined"
							color="primary"
							id="local-ip-address-input"
							size="small"
							value={deviceDetails?.device_local_ip ?? ""}
							slotProps={{ input: { readOnly: true } }}
							autoComplete="off"
						/>
					</Grid2>

					<Grid2 size={{ xs: 12, md: 5, xl: 4 }}>
						<InputLabel htmlFor="global-ip-address-input" sx={{ fontWeight: 500, marginBottom: "0.5rem" }}>
							Global IP
						</InputLabel>

						<TextField
							fullWidth
							variant="outlined"
							color="primary"
							id="global-ip-address-input"
							size="small"
							value={deviceDetails?.device_global_ip ?? ""}
							slotProps={{ input: { readOnly: true } }}
							autoComplete="off"
						/>
					</Grid2>

					<Grid2 size={{ xs: 12, md: 5, xl: 4 }}>
						<InputLabel htmlFor="rto-input" sx={{ fontWeight: 500, marginBottom: "0.5rem" }}>
							RTO
						</InputLabel>

						<TextField
							fullWidth
							variant="outlined"
							color="primary"
							id="rto-input"
							size="small"
							value={selectedJurisdiction?.name || ""}
							slotProps={{ input: { readOnly: true } }}
							autoComplete="off"
						/>
					</Grid2>
				</Grid2>
			</Box>

			<Divider sx={{ margin: "1.5rem 0 1rem" }} />

			<Box className="device-settings-section">
				<Typography variant="h6" color="var(--color-primary-main)">
					Health Check
				</Typography>

				<Grid2 container spacing={2}>
					<Grid2 size={{ xs: 12, md: 6 }}>
						<Typography fontWeight={500}>System Temperature</Typography>

						<Divider sx={{ margin: "1rem 0" }} />

						<Box className="device-health-check-table-wrapper">
							<AGDataGrid
								getRowId={(item) => item.temperature}
								sx={{ height: "250px" }}
								columns={systemTemperatureColumnsData}
								rows={deviceDetails?.system_temperature ?? []}
								columnHeaderHeight={60}
								slots={{ footer: () => <></> }}
							/>
						</Box>
					</Grid2>

					<Grid2 size={{ xs: 12, md: 6 }}>
						<Typography fontWeight={500}>Memory Usage</Typography>

						<Divider sx={{ margin: "1rem 0" }} />

						<Box className="device-health-check-table-wrapper">
							<AGDataGrid
								getRowId={(item) => item.type}
								sx={{ height: "250px" }}
								columns={memoryUsageColumnsData}
								rows={deviceDetails?.memory_usage ?? []}
								columnHeaderHeight={60}
								slots={{ footer: () => <></> }}
							/>
						</Box>
					</Grid2>

					<Grid2 size={{ xs: 12, md: 6 }}>
						<Typography fontWeight={500}>Software Status</Typography>

						<Divider sx={{ margin: "1rem 0" }} />

						<Box className="device-health-check-table-wrapper">
							<AGDataGrid
								getRowId={(item) => item.software}
								sx={{ height: "250px" }}
								columns={softwareStatusColumnsData}
								rows={deviceDetails?.software_status ?? []}
								columnHeaderHeight={60}
								slots={{ footer: () => <></> }}
							/>
						</Box>
					</Grid2>
				</Grid2>
			</Box>
		</AGDialog>
	);
};

export default DeviceSettingsDialog;
